/**
 * aigo-essentials.js — Restaurant Everyday Essentials page interactive module
 *
 * Handles: filter chips, panel expand/collapse, subcategory chips,
 * show-all caps, quantity steppers, cart integration.
 */
(function () {
  'use strict';

  // ── Config (from #essentials-config script tag) ───────────────────────────────
  var config = (function () {
    var el = document.getElementById('essentials-config');
    try { return el ? JSON.parse(el.textContent) : {}; } catch (e) { return {}; }
  })();

  // ── Constants ────────────────────────────────────────────────────────────────
  var MOBILE_BREAKPOINT = 860;
  var DESKTOP_CAP = config.desktopCap || 6;
  var MOBILE_CAP  = config.mobileCap  || 4;
  var ESS_TAG     = config.essTag     || 'everyday_essentials';

  // ── State ────────────────────────────────────────────────────────────────────
  var selectedCats = new Set();   // data-cat-idx values currently active
  var totalPanels  = 0;           // snapshot of total visible panels (set on init)
  var variantQty   = {};          // { variantId: qty }
  var variantKey   = {};          // { variantId: lineItemKey }
  var pending      = {};          // { variantId: true } — in-flight guard
  var savedQty     = {};          // { variantId: qty } — pre-optimistic, for rollback

  // ── Cart helpers ─────────────────────────────────────────────────────────────

  function fetchCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function rebuildMap(cart) {
    variantQty = {};
    variantKey = {};
    (cart.items || []).forEach(function (item) {
      var vid = String(item.variant_id);
      variantQty[vid] = (variantQty[vid] || 0) + item.quantity;
      variantKey[vid] = item.key;
    });
  }

  // ── syncCard ─────────────────────────────────────────────────────────────────

  function syncCard(variantId, qty) {
    var vid = String(variantId);
    var qtyEls = document.querySelectorAll('.ec__qty[data-variant-id="' + vid + '"]');
    qtyEls.forEach(function (qtyEl) {
      var card      = qtyEl.closest('.ec');
      var addBtn    = qtyEl.querySelector('.ecq__add');
      var countEl   = qtyEl.querySelector('.ecq__n');
      var minusBtn  = qtyEl.querySelector('.ecq__b:not(.ecq__b--on)');
      var plusBtn   = qtyEl.querySelector('.ecq__b--on');
      var trashSvg  = qtyEl.querySelector('.ecq__trash');
      var minusSvg  = qtyEl.querySelector('.ecq__minus');

      if (qty <= 0) {
        if (addBtn)    addBtn.style.display    = '';
        if (minusBtn)  minusBtn.style.display  = 'none';
        if (plusBtn)   plusBtn.style.display   = 'none';
        if (countEl)   countEl.style.display   = 'none';
        if (card)      card.classList.remove('is-in');
      } else {
        if (addBtn)    addBtn.style.display    = 'none';
        if (minusBtn)  minusBtn.style.display  = '';
        if (plusBtn)   plusBtn.style.display   = '';
        if (countEl) {
          countEl.style.display  = '';
          countEl.textContent    = qty;
        }
        if (card) card.classList.add('is-in');

        // Swap trash / minus icons
        if (trashSvg) trashSvg.style.display = (qty === 1) ? '' : 'none';
        if (minusSvg) minusSvg.style.display = (qty === 1) ? 'none' : '';
      }

      qtyEl.dataset.qty = qty;
    });

    updateBadges();
  }

  // ── updateBadges ─────────────────────────────────────────────────────────────

  function updateBadges() {
    document.querySelectorAll('.cpanel').forEach(function (panel) {
      var catIdx = panel.dataset.catIdx;
      var badge  = document.querySelector('.cpanel__badge[data-cat-idx="' + catIdx + '"]');
      if (!badge) return;
      var count = panel.querySelectorAll('.ec.is-in').length;
      badge.textContent = count + ' added';
      if (count > 0) {
        badge.classList.add('hit');
      } else {
        badge.classList.remove('hit');
      }
    });
  }

  // ── Filter chips ─────────────────────────────────────────────────────────────

  function handleFilterChip(e) {
    var chip = e.target.closest('.fchip');
    if (!chip) return;

    var catIdx = chip.dataset.catIdx;
    if (chip.classList.contains('on')) {
      chip.classList.remove('on');
      selectedCats.delete(catIdx);
    } else {
      chip.classList.add('on');
      selectedCats.add(catIdx);
    }

    applyFilters();
  }

  function handleFilterClear() {
    // Remove 'on' from all chips
    document.querySelectorAll('.fchip.on').forEach(function (c) { c.classList.remove('on'); });
    selectedCats = new Set();
    applyFilters();
  }

  function applyFilters() {
    var panels   = document.querySelectorAll('.cpanel');
    var fcount   = document.querySelector('.fcount');
    var fclearEl = document.querySelector('.fclear');
    var size     = selectedCats.size;

    panels.forEach(function (panel) {
      var idx = panel.dataset.catIdx;
      if (size === 0 || selectedCats.has(idx)) {
        panel.classList.remove('hidden');
      } else {
        panel.classList.add('hidden');
      }
    });

    // Auto-expand when 1 or 2 categories selected
    if (size === 1 || size === 2) {
      panels.forEach(function (panel) {
        if (selectedCats.has(panel.dataset.catIdx)) {
          panel.classList.add('open');
          var hBtn = panel.querySelector('.cpanel__h');
          if (hBtn) hBtn.setAttribute('aria-expanded', 'true');
          loadPanelBody(panel);
        }
      });
    }

    // Update count label
    if (fcount) {
      if (size === 0) {
        fcount.textContent = totalPanels + ' shown';
      } else {
        var visible = Array.from(panels).filter(function (p) {
          return selectedCats.has(p.dataset.catIdx);
        }).length;
        fcount.textContent = visible + ' shown';
      }
    }

    // Show / hide clear button
    if (fclearEl) {
      fclearEl.style.display = size > 0 ? '' : 'none';
    }
  }

  // ── Lazy panel loading ────────────────────────────────────────────────────────

  /** HTML-escape a value for safe injection into innerHTML. */
  function esc(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /** Only allow hex color values — prevents style injection via --cat-color. */
  function sanitizeColor(color) {
    return /^#[0-9a-fA-F]{3,6}$/.test(color) ? color : '#8A9891';
  }

  /** Format cents (integer) or dollar string to "$X.XX". */
  function formatMoney(price) {
    // products.json returns price as a string like "12.99"
    var n = parseFloat(price);
    if (isNaN(n)) return price;
    return '$' + n.toFixed(2);
  }

  /** Resize a Shopify CDN image URL to the requested pixel width. */
  function shopifyImg(src, width) {
    if (!src) return null;
    return src.replace(/(\.[a-zA-Z]+)(\?|$)/, '_' + width + 'x$1$2');
  }

  /** Login-guard attributes for stepper buttons. */
  var LOGIN_ATTRS = config.loginRequired
    ? 'data-login-required="true" data-login-url="' + (config.loginUrl || '/account/login') + '"'
    : '';

  /** Build HTML for one product card. Mirrors aigo-essentials-card.liquid. */
  function buildCardHTML(product) {
    var catColor  = sanitizeColor(product._catColor || '#8A9891');
    var variant   = product.variants && product.variants[0];
    if (!variant) return '';

    var vid       = Number(variant.id);
    var capPrice  = variant.compare_at_price;
    var salePct   = 0;
    if (capPrice && parseFloat(capPrice) > parseFloat(variant.price)) {
      salePct = Math.round((parseFloat(capPrice) - parseFloat(variant.price)) / parseFloat(capPrice) * 100);
    }

    var imgSrc     = product.images && product.images[0] && shopifyImg(product.images[0].src, 400);
    var thumbInner = imgSrc
      ? '<img src="' + esc(imgSrc) + '" alt="" width="400" height="300" loading="lazy">'
      : '';

    var flagHTML = salePct > 0
      ? '<span class="ec__flag ec__flag--promo">-' + salePct + '%</span>'
      : '';

    var invQty   = parseInt(variant.inventory_quantity, 10);
    var lowStock = (
      variant.inventory_management === 'shopify' && invQty > 0 && invQty <= 5
    ) ? '<div class="ec__unit"><span class="ec__low">Only ' + invQty + ' left</span></div>' : '';

    var titleEsc = esc(product.title);

    return (
      '<div class="ec">' +
        '<div class="ec__thumb no-empty" style="background:linear-gradient(140deg,' + catColor + '33,#fff 70%)">' +
          thumbInner +
          flagHTML +
        '</div>' +
        '<div class="ec__body">' +
          '<h3 class="ec__name">' + titleEsc + '</h3>' +
          lowStock +
        '</div>' +
        '<div class="ec__foot">' +
          '<div class="ec__qty" data-variant-id="' + vid + '">' +
            '<button class="ecq__add" type="button" ' + LOGIN_ATTRS + ' aria-label="Add ' + titleEsc + ' to cart">' +
              '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>' +
            '</button>' +
            '<button class="ecq__b" type="button" data-action="decrement" ' + LOGIN_ATTRS + ' aria-label="Remove one ' + titleEsc + '">' +
              '<svg class="ecq__trash" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>' +
              '<svg class="ecq__minus" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12"/></svg>' +
            '</button>' +
            '<span class="ecq__n">0</span>' +
            '<button class="ecq__b ecq__b--on" type="button" data-action="increment" ' + LOGIN_ATTRS + ' aria-label="Add one more ' + titleEsc + '">' +
              '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>' +
            '</button>' +
          '</div>' +
        '</div>' +
      '</div>'
    );
  }

  /** Build a full .subgroup element for one sub-collection. */
  function buildSubgroupHTML(sub, products, catColor) {
    var cap = window.innerWidth <= MOBILE_BREAKPOINT ? MOBILE_CAP : DESKTOP_CAP;

    var gridItems = products.map(function (p, i) {
      p._catColor = catColor;
      var overCap = i >= cap;
      return '<div' + (overCap ? ' class="ec--over-cap"' : '') + '>' + buildCardHTML(p) + '</div>';
    }).join('');

    var showAllBtn = products.length > cap
      ? '<button class="showall" type="button" data-sub="' + esc(sub.handle) + '">' +
          'Show all ' + products.length + ' in ' + esc(sub.title) +
          '<span>+' + (products.length - cap) + '</span>' +
        '</button>'
      : '';

    return (
      '<div class="subgroup" data-sub="' + esc(sub.handle) + '">' +
        '<div class="subrow">' +
          '<span class="subrow__title">' + esc(sub.title) + '</span>' +
          '<div class="subrow__rule no-empty"></div>' +
          '<span class="subrow__count">' + products.length + '</span>' +
        '</div>' +
        '<div class="egrid" data-cap-desktop="' + DESKTOP_CAP + '" data-cap-mobile="' + MOBILE_CAP + '">' +
          gridItems +
        '</div>' +
        showAllBtn +
      '</div>'
    );
  }

  /**
   * Fetch products for a sub-collection handle, filter by ESS_TAG.
   * Returns a promise that resolves to a filtered product array.
   */
  function fetchSubProducts(handle) {
    return fetch('/collections/' + handle + '/products.json?limit=250', {
      headers: { Accept: 'application/json' }
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        return (data.products || []).filter(function (p) {
          return Array.isArray(p.tags) && p.tags.indexOf(ESS_TAG) !== -1;
        });
      });
  }

  /**
   * Load a panel body for the first time: fetch products, build HTML, inject.
   * Sets data-loaded="true" on success so repeat opens skip the fetch.
   * @param {string} [activeSub] — if set, hide all subgroups except this handle after load.
   */
  function loadPanelBody(panel, activeSub) {
    var body = panel.querySelector('.cpanel__body');
    if (!body || body.dataset.loaded !== 'false') return;

    // Mark in-flight to prevent double fetch
    body.dataset.loaded = 'loading';

    var catColor = panel.style.getPropertyValue('--cat-color').trim() || '#8A9891';

    // Read sub-collection stubs from embedded JSON script tags
    var stubs = Array.from(body.querySelectorAll('.subgroup-data')).map(function (el) {
      try { return JSON.parse(el.textContent); } catch (e) { return null; }
    }).filter(Boolean);

    if (stubs.length === 0) {
      body.dataset.loaded = 'true';
      return;
    }

    // Show loading state
    body.innerHTML = '<p class="ess-loading">Loading\u2026</p>';

    Promise.all(stubs.map(function (sub) {
      return fetchSubProducts(sub.handle).then(function (products) {
        return { sub: sub, products: products };
      });
    }))
      .then(function (results) {
        var html = results
          .filter(function (r) { return r.products.length > 0; })
          .map(function (r) { return buildSubgroupHTML(r.sub, r.products, catColor); })
          .join('');

        body.innerHTML = html || '<p class="ess-loading">No essentials products found.</p>';
        body.dataset.loaded = 'true';

        // Apply subcategory filter if a chip was active when the panel opened
        if (activeSub) {
          body.querySelectorAll('.subgroup').forEach(function (sg) {
            sg.style.display = sg.dataset.sub === activeSub ? '' : 'none';
          });
        }

        // Hydrate newly injected cards with current cart state
        body.querySelectorAll('.ec__qty[data-variant-id]').forEach(function (el) {
          syncCard(el.dataset.variantId, variantQty[el.dataset.variantId] || 0);
        });
      })
      .catch(function (err) {
        console.error('[essentials] Failed to load panel:', err);
        body.innerHTML = '<p class="ess-loading">Failed to load products. Please refresh.</p>';
        body.dataset.loaded = 'false'; // allow retry
      });
  }

  // ── Panel toggle ─────────────────────────────────────────────────────────────

  function handlePanelToggle(e) {
    var hBtn  = e.target.closest('.cpanel__h');
    if (!hBtn) return;
    var panel = hBtn.closest('.cpanel');
    if (!panel) return;

    var isOpen = panel.classList.toggle('open');
    hBtn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

    if (isOpen) {
      loadPanelBody(panel);
    } else {
      // When closing — clear any active schip and show all subgroups
      panel.querySelectorAll('.schip.on').forEach(function (c) { c.classList.remove('on'); });
      panel.querySelectorAll('.subgroup').forEach(function (sg) { sg.style.display = ''; });
    }
  }

  // ── Subcategory chips ─────────────────────────────────────────────────────────

  function handleSubchip(e) {
    var chip  = e.target.closest('.schip');
    if (!chip) return;
    var panel = chip.closest('.cpanel');
    if (!panel) return;

    var sub       = chip.dataset.sub;
    var wasActive = chip.classList.contains('on');

    // Remove 'on' from all schips in this panel
    panel.querySelectorAll('.schip.on').forEach(function (c) { c.classList.remove('on'); });

    var panelWasClosed = !panel.classList.contains('open');

    // Open panel if closed
    if (panelWasClosed) {
      panel.classList.add('open');
      var hBtn = panel.querySelector('.cpanel__h');
      if (hBtn) hBtn.setAttribute('aria-expanded', 'true');
    }

    if (wasActive) {
      // Toggle off — show all subgroups
      if (panelWasClosed) {
        // Panel just opened with no active filter — load all
        loadPanelBody(panel);
      } else {
        panel.querySelectorAll('.subgroup').forEach(function (sg) { sg.style.display = ''; });
      }
    } else {
      // Toggle on
      chip.classList.add('on');
      var body = panel.querySelector('.cpanel__body');
      if (panelWasClosed || (body && body.dataset.loaded !== 'true')) {
        // Body not ready yet — pass activeSub so load applies the filter after fetch
        loadPanelBody(panel, sub);
      } else {
        // Body already loaded — filter synchronously
        panel.querySelectorAll('.subgroup').forEach(function (sg) {
          sg.style.display = (sg.dataset.sub === sub) ? '' : 'none';
        });
      }
    }
  }

  // ── Show all ─────────────────────────────────────────────────────────────────

  function handleShowAll(e) {
    var btn      = e.target.closest('.showall');
    if (!btn) return;
    // .showall is a sibling of .egrid inside .subgroup, not a descendant
    var subgroup = btn.closest('.subgroup');
    if (!subgroup) return;
    var egrid    = subgroup.querySelector('.egrid');
    if (!egrid) return;

    // Remove ec--over-cap from wrapper divs (direct children of egrid)
    Array.from(egrid.children).forEach(function (wrapper) {
      wrapper.classList.remove('ec--over-cap');
    });
    btn.parentNode.removeChild(btn);
  }

  // initMobileCaps is no longer needed — mobile cap is applied at card build time
  // inside buildSubgroupHTML(), which reads window.innerWidth when the panel opens.

  // ── Cart init (hydrate) ───────────────────────────────────────────────────────

  function initCart() {
    var cached = window.FoxTheme && FoxTheme.Cart && FoxTheme.Cart.getState();
    if (cached) {
      rebuildMap(cached);
      hydrateAll();
    } else {
      fetchCart()
        .then(function (cart) { rebuildMap(cart); hydrateAll(); })
        .catch(function (err) { console.warn('[essentials] Cart fetch failed:', err); });
    }
  }

  function hydrateAll() {
    document.querySelectorAll('.ec__qty[data-variant-id]').forEach(function (el) {
      var vid = el.dataset.variantId;
      syncCard(vid, variantQty[vid] || 0);
    });
  }

  // ── Guest guard ───────────────────────────────────────────────────────────────

  function isGuestGuarded(btn) {
    if (btn.dataset.loginRequired !== 'true') return false;
    // Prefer the login URL baked into the button (customer_identity/login for new accounts).
    // Fall back to body data-login-url, then the classic /account/login route.
    var base = btn.dataset.loginUrl || document.body.dataset.loginUrl || '/account/login';
    window.location.href = base + '?return_to=' + encodeURIComponent(window.location.pathname + window.location.search);
    return true;
  }

  // ── Stepper: add ─────────────────────────────────────────────────────────────

  function handleAdd(e) {
    var btn    = e.target.closest('.ecq__add');
    if (!btn) return;
    var qtyEl  = btn.closest('.ec__qty[data-variant-id]');
    if (!qtyEl) return;
    var vid    = String(qtyEl.dataset.variantId);

    if (isGuestGuarded(btn)) return;

    if (pending[vid]) return;
    if (!window.FoxTheme || !FoxTheme.Cart) {
      console.warn('[essentials] FoxTheme.Cart not available');
      return;
    }
    pending[vid] = true;

    var prev       = variantQty[vid] || 0;
    savedQty[vid]  = prev;
    variantQty[vid] = prev + 1;
    syncCard(vid, prev + 1);

    FoxTheme.Cart.add([{ id: parseInt(vid, 10), quantity: 1 }])
      .then(
        function (result) {
          rebuildMap(result.state);
          // Sync all cards that display this variant
          syncCard(vid, variantQty[vid] || 0);
        },
        function (err) {
          console.error('[essentials] Add error:', err);
          variantQty[vid] = savedQty[vid];
          syncCard(vid, savedQty[vid]);
        }
      )
      .finally(function () { delete pending[vid]; });
  }

  // ── Stepper: increment / decrement ───────────────────────────────────────────

  function handleStep(e, delta) {
    // delta: +1 for increment (.ecq__b--on), -1 for decrement (.ecq__b:not(.ecq__b--on))
    var btn   = delta > 0
      ? e.target.closest('.ecq__b--on')
      : e.target.closest('.ecq__b');
    if (!btn) return;
    if (delta < 0 && btn.classList.contains('ecq__b--on')) return; // safety: skip + side
    var qtyEl = btn.closest('.ec__qty[data-variant-id]');
    if (!qtyEl) return;
    var vid   = String(qtyEl.dataset.variantId);

    if (isGuestGuarded(btn)) return;

    if (pending[vid]) return;
    if (!window.FoxTheme || !FoxTheme.Cart) {
      console.warn('[essentials] FoxTheme.Cart not available');
      return;
    }
    pending[vid] = true;

    var current     = variantQty[vid] || 0;
    var newQty      = current + delta;
    savedQty[vid]   = current;
    variantQty[vid] = newQty;
    syncCard(vid, newQty);

    var lineKey = variantKey[vid];

    function doChange(key) {
      FoxTheme.Cart.change(key, newQty)
        .then(
          function (result) {
            rebuildMap(result.state);
            syncCard(vid, variantQty[vid] || 0);
          },
          function (err) {
            console.error('[essentials] Step error:', err);
            variantQty[vid] = savedQty[vid];
            syncCard(vid, savedQty[vid]);
          }
        )
        .finally(function () { delete pending[vid]; });
    }

    if (lineKey) {
      doChange(lineKey);
    } else {
      fetchCart().then(function (cart) {
        rebuildMap(cart);
        var key = variantKey[vid];
        if (key) {
          doChange(key);
        } else {
          // Item not in cart — roll back or treat as qty 0 for decrement
          variantQty[vid] = delta < 0 ? 0 : savedQty[vid];
          syncCard(vid, variantQty[vid]);
          delete pending[vid];
        }
      }).catch(function () {
        variantQty[vid] = savedQty[vid];
        syncCard(vid, savedQty[vid]);
        delete pending[vid];
      });
    }
  }

  // ── Click router ─────────────────────────────────────────────────────────────

  function handleClick(e) {
    if (e.target.closest('.fchip'))           { handleFilterChip(e);  return; }
    if (e.target.closest('.fclear'))          { handleFilterClear();   return; }
    if (e.target.closest('.cpanel__h'))       { handlePanelToggle(e); return; }
    if (e.target.closest('.schip'))           { handleSubchip(e);     return; }
    if (e.target.closest('.showall'))         { handleShowAll(e);     return; }
    if (e.target.closest('.ecq__add'))        { handleAdd(e);         return; }
    // Increment (+) must be checked before decrement — .ecq__b--on is a subset of .ecq__b
    if (e.target.closest('.ecq__b--on'))      { handleStep(e,  1);    return; }
    if (e.target.closest('.ecq__b:not(.ecq__b--on)')) { handleStep(e, -1); return; }
  }

  // ── External cart sync ────────────────────────────────────────────────────────

  function setupCartSubscription() {
    if (!window.FoxTheme || !window.FoxTheme.Cart) {
      // Fallback: listen for legacy cart:refresh event
      document.addEventListener('cart:refresh', function () {
        fetchCart().then(function (c) { rebuildMap(c); hydrateAll(); });
      });
      return;
    }

    FoxTheme.Cart.subscribe(function (payload) {
      // Skip rehydration while a local mutation is in flight
      if (Object.keys(pending).length > 0) return;
      var cart = payload.state;
      if (cart) {
        rebuildMap(cart);
        hydrateAll();
      } else {
        fetchCart().then(function (c) { rebuildMap(c); hydrateAll(); });
      }
    });
  }

  // ── Init ─────────────────────────────────────────────────────────────────────

  function init() {
    // Snapshot total panel count for the fcount label
    totalPanels = document.querySelectorAll('.cpanel').length;

    // Initialise clear button state (hidden on load)
    var fclearEl = document.querySelector('.fclear');
    if (fclearEl) fclearEl.style.display = 'none';

    // Set initial fcount text
    var fcount = document.querySelector('.fcount');
    if (fcount) fcount.textContent = totalPanels + ' shown';

    // Cart hydration
    initCart();

    // External cart sync
    setupCartSubscription();

    // Single delegated listener for all interactions
    document.addEventListener('click', handleClick);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Browser back/forward cache restore
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) {
      var cart = window.FoxTheme && FoxTheme.Cart && FoxTheme.Cart.getState();
      if (cart) { rebuildMap(cart); hydrateAll(); }
      else { fetchCart().then(function (c) { rebuildMap(c); hydrateAll(); }); }
    }
  });
})();
